// src/components/games/crossroads/TargetSelector.tsx
import React, { useState } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonText,
  IonBadge,
  IonIcon,
} from '@ionic/react';
import { checkmarkCircle, closeCircle } from 'ionicons/icons';
import { GameState, PendingTargetSelection, TargetSelection } from './types';
import { ALL_CARDS } from './cardData';
import './TargetSelector.css';

interface TargetSelectorProps {
  gameState: GameState;
  targetRequest: PendingTargetSelection;
  onComplete: (selection: TargetSelection) => void;
  onCancel: () => void;
}

export const TargetSelector: React.FC<TargetSelectorProps> = ({
  gameState,
  targetRequest,
  onComplete,
  onCancel,
}) => {
  const [selectedCreatures, setSelectedCreatures] = useState<string[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<number[]>([]);

  const canTarget = targetRequest.targetType;
  const totalSelected = selectedCreatures.length + selectedPlayers.length;
  const canConfirm = totalSelected >= targetRequest.minTargets && totalSelected <= targetRequest.maxTargets;

  const toggleCreature = (creatureId: string) => {
    if (selectedCreatures.includes(creatureId)) {
      setSelectedCreatures(selectedCreatures.filter(id => id !== creatureId));
    } else {
      if (totalSelected < targetRequest.maxTargets) {
        setSelectedCreatures([...selectedCreatures, creatureId]);
      }
    }
  };

  const togglePlayer = (playerId: number) => {
    if (selectedPlayers.includes(playerId)) {
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
    } else {
      if (totalSelected < targetRequest.maxTargets) {
        setSelectedPlayers([...selectedPlayers, playerId]);
      }
    }
  };

  const handleConfirm = () => {
    if (canConfirm) {
      const selection: TargetSelection = {};
      if (selectedCreatures.length > 0) selection.creatures = selectedCreatures;
      if (selectedPlayers.length > 0) selection.players = selectedPlayers;
      onComplete(selection);
    }
  };

  const handleCancel = () => {
    if (targetRequest.onCancel) {
      targetRequest.onCancel();
    }
    onCancel();
  };

  // Get all valid creature targets
  const getValidCreatures = () => {
    const allCreatures = gameState.players.flatMap(player =>
      player.battlefield.creatures.map(creature => ({
        ...creature,
        ownerId: player.id,
      }))
    );

    return allCreatures.filter(creature => {
      // Apply custom filter if provided
      if (targetRequest.filter && !targetRequest.filter(creature.instanceId, gameState)) {
        return false;
      }
      return true;
    });
  };

  // Get valid player targets
  const getValidPlayers = () => {
    return gameState.players.filter(player => {
      // Apply custom filter if provided
      if (targetRequest.filter && !targetRequest.filter(`player_${player.id}`, gameState)) {
        return false;
      }
      return true;
    });
  };

  const validCreatures = getValidCreatures();
  const validPlayers = getValidPlayers();

  return (
    <div className="target-selector-overlay">
      <IonCard className="target-selector-card">
        <IonCardHeader>
          <IonCardTitle className="target-selector-title">
            {targetRequest.title}
          </IonCardTitle>
        </IonCardHeader>

        <IonCardContent>
          <IonText className="target-selector-description">
            <p>{targetRequest.description}</p>
            <p>
              <IonBadge color="primary">
                {totalSelected} / {targetRequest.maxTargets} selected
              </IonBadge>
              {targetRequest.minTargets > 0 && (
                <IonText color="medium" style={{ marginLeft: '8px', fontSize: '0.9em' }}>
                  (Minimum: {targetRequest.minTargets})
                </IonText>
              )}
            </p>
          </IonText>

          {/* Creature targets */}
          {(canTarget === 'creature' || canTarget === 'any') && validCreatures.length > 0 && (
            <div className="target-section">
              <h3>Creatures</h3>
              <div className="target-list">
                {validCreatures.map(creature => {
                  const card = ALL_CARDS[creature.cardId];
                  const isSelected = selectedCreatures.includes(creature.instanceId);
                  const isDisabled = !isSelected && totalSelected >= targetRequest.maxTargets;

                  return (
                    <div
                      key={creature.instanceId}
                      className={`target-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => !isDisabled && toggleCreature(creature.instanceId)}
                    >
                      <div className="target-info">
                        <strong>{card?.name || 'Unknown'}</strong>
                        <IonText color="medium" style={{ fontSize: '0.85em' }}>
                          {creature.power}/{creature.health}
                          {creature.ownerId === gameState.currentPlayerIndex ? ' (Yours)' : ' (Opponent)'}
                        </IonText>
                      </div>
                      {isSelected && <IonIcon icon={checkmarkCircle} color="success" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Player targets */}
          {(canTarget === 'player' || canTarget === 'any') && validPlayers.length > 0 && (
            <div className="target-section">
              <h3>Players</h3>
              <div className="target-list">
                {validPlayers.map(player => {
                  const isSelected = selectedPlayers.includes(player.id);
                  const isDisabled = !isSelected && totalSelected >= targetRequest.maxTargets;

                  return (
                    <div
                      key={player.id}
                      className={`target-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => !isDisabled && togglePlayer(player.id)}
                    >
                      <div className="target-info">
                        <strong>{player.name}</strong>
                        <IonText color="medium" style={{ fontSize: '0.85em' }}>
                          HP: {player.commander.currentHealth}/{player.commander.maxHealth}
                        </IonText>
                      </div>
                      {isSelected && <IonIcon icon={checkmarkCircle} color="success" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="target-selector-actions">
            <IonButton expand="block" color="success" disabled={!canConfirm} onClick={handleConfirm}>
              <IonIcon slot="start" icon={checkmarkCircle} />
              Confirm ({totalSelected} target{totalSelected !== 1 ? 's' : ''})
            </IonButton>
            <IonButton expand="block" color="medium" fill="outline" onClick={handleCancel}>
              <IonIcon slot="start" icon={closeCircle} />
              Cancel
            </IonButton>
          </div>
        </IonCardContent>
      </IonCard>
    </div>
  );
};
